/**
 * Workflow Identifier Generator
 *
 * Generates memorable 4-word identifiers for workflows like:
 * "swift-blue-river-flows"
 * "happy-green-mountain-stands"
 *
 * Format: [adjective]-[adjective]-[noun]-[verb]
 */

const adjectives = [
  'swift', 'bright', 'calm', 'bold', 'wise', 'pure', 'keen', 'vast',
  'gentle', 'fierce', 'clever', 'noble', 'silent', 'golden', 'silver',
  'crystal', 'ancient', 'modern', 'happy', 'serene', 'vivid', 'cosmic',
  'quantum', 'stellar', 'lunar', 'solar', 'arctic', 'tropic', 'alpine',
  'ocean', 'forest', 'desert', 'prairie', 'mystic', 'heroic', 'epic',
  'grand', 'prime', 'ultra', 'mega', 'super', 'hyper', 'cyber', 'digital',
  'neural', 'atomic', 'blazing', 'frozen', 'electric', 'magnetic'
];

const nouns = [
  'river', 'mountain', 'ocean', 'forest', 'valley', 'canyon', 'glacier',
  'meadow', 'creek', 'peak', 'summit', 'ridge', 'cliff', 'shore', 'wave',
  'storm', 'thunder', 'lightning', 'sunrise', 'sunset', 'aurora', 'comet',
  'star', 'moon', 'sun', 'planet', 'galaxy', 'nebula', 'cosmos', 'void',
  'phoenix', 'dragon', 'falcon', 'eagle', 'hawk', 'raven', 'wolf', 'bear',
  'tiger', 'lion', 'panther', 'leopard', 'cheetah', 'jaguar', 'lynx',
  'cipher', 'matrix', 'vector', 'tensor', 'quantum', 'photon', 'neuron',
  'nexus', 'portal', 'gateway', 'bridge', 'tower', 'fortress', 'citadel'
];

const verbs = [
  'flows', 'soars', 'shines', 'rises', 'falls', 'runs', 'flies', 'glides',
  'drifts', 'races', 'climbs', 'leaps', 'bounds', 'springs', 'surges',
  'blazes', 'gleams', 'sparkles', 'glows', 'radiates', 'beams', 'flashes',
  'strikes', 'roars', 'whispers', 'echoes', 'resonates', 'pulses', 'throbs',
  'beats', 'pounds', 'drums', 'rings', 'chimes', 'sings', 'calls', 'cries',
  'howls', 'rumbles', 'crashes', 'breaks', 'shatters', 'bursts', 'erupts',
  'emerges', 'appears', 'manifests', 'transforms', 'evolves', 'adapts'
];

/**
 * Generate a random 4-word workflow identifier
 * Format: [adjective]-[adjective]-[noun]-[verb]
 *
 * @returns {string} A memorable workflow identifier
 */
export function generateWorkflowIdentifier() {
  const adj1 = adjectives[Math.floor(Math.random() * adjectives.length)];
  const adj2 = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const verb = verbs[Math.floor(Math.random() * verbs.length)];

  return `${adj1}-${adj2}-${noun}-${verb}`;
}

/**
 * Generate an intelligent filename based on content
 * Uses AI/pattern matching to determine appropriate name
 *
 * @param {string} content - The file content
 * @param {string} nodeType - Type of node that generated the content
 * @param {string} displayName - Node display name
 * @returns {string} Suggested filename without extension
 */
export function generateIntelligentFilename(content, nodeType, displayName) {
  if (!content) return 'output';

  const contentLower = content.toLowerCase();
  const firstLine = content.split('\n')[0].substring(0, 50);

  // Try to detect content type based on patterns
  if (contentLower.includes('summary') || contentLower.includes('executive summary')) {
    return 'summary';
  }

  if (contentLower.includes('report') || contentLower.includes('analysis')) {
    return 'report';
  }

  if (contentLower.includes('recommendation') || contentLower.includes('suggest')) {
    return 'recommendations';
  }

  if (contentLower.includes('decision') || contentLower.includes('conclusion')) {
    return 'decision';
  }

  if (contentLower.includes('action') || contentLower.includes('next steps')) {
    return 'action-plan';
  }

  if (contentLower.includes('data') && contentLower.includes('extract')) {
    return 'extracted-data';
  }

  if (contentLower.includes('validation') || contentLower.includes('verified')) {
    return 'validation-results';
  }

  // Check for list-like content
  const bulletPoints = (content.match(/^[\s]*[-*•]\s/gm) || []).length;
  const numberedList = (content.match(/^[\s]*\d+[\.)]\s/gm) || []).length;

  if (bulletPoints > 3) {
    return 'list';
  }

  if (numberedList > 3) {
    return 'numbered-list';
  }

  // Check for table-like content
  const pipes = (content.match(/\|/g) || []).length;
  if (pipes > 10) {
    return 'table';
  }

  // Check for code-like content
  const codeIndicators = /```|function |class |const |let |var |import |export /;
  if (codeIndicators.test(content)) {
    return 'code';
  }

  // Check for JSON
  try {
    JSON.parse(content);
    return 'data';
  } catch (e) {
    // Not JSON
  }

  // Use first few words of content as filename
  const words = firstLine
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .toLowerCase()
    .split(/\s+/)
    .filter(w => w.length > 3)
    .slice(0, 3);

  if (words.length > 0) {
    return words.join('-');
  }

  // Fallback to node info
  if (displayName) {
    return displayName
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 30);
  }

  // Last resort based on node type
  const nodeTypeNames = {
    llm_analysis: 'analysis',
    data_retrieval: 'data',
    reconciliation: 'reconciliation',
    human_approval: 'approved',
    decomposition: 'breakdown'
  };

  return nodeTypeNames[nodeType] || 'output';
}

/**
 * Format workflow identifier for display
 *
 * @param {string} identifier - The workflow identifier
 * @returns {string} Formatted display string
 */
export function formatWorkflowIdentifier(identifier) {
  if (!identifier) return 'Unknown Workflow';

  return identifier
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get workflow identifier from workflow object
 * Generates one if it doesn't exist
 *
 * @param {Object} workflow - Workflow object
 * @returns {string} Workflow identifier
 */
export function getWorkflowIdentifier(workflow) {
  if (!workflow) return generateWorkflowIdentifier();

  // Check if workflow already has an identifier
  if (workflow.identifier || workflow.workflow_identifier) {
    return workflow.identifier || workflow.workflow_identifier;
  }

  // Generate based on workflow ID (deterministic if possible)
  if (workflow.id) {
    // Use workflow ID to seed the random generation for consistency
    // This ensures the same workflow always gets the same identifier
    const seed = hashCode(workflow.id);
    return generateDeterministicIdentifier(seed);
  }

  // Generate random if no ID
  return generateWorkflowIdentifier();
}

/**
 * Simple hash function for string
 */
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate deterministic identifier from seed
 */
function generateDeterministicIdentifier(seed) {
  const seededRandom = (max) => {
    seed = (seed * 9301 + 49297) % 233280;
    return Math.floor((seed / 233280) * max);
  };

  const adj1 = adjectives[seededRandom(adjectives.length)];
  const adj2 = adjectives[seededRandom(adjectives.length)];
  const noun = nouns[seededRandom(nouns.length)];
  const verb = verbs[seededRandom(verbs.length)];

  return `${adj1}-${adj2}-${noun}-${verb}`;
}
